import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Label } from './Label'
import { cn, getChildrenArray } from '@/lib/utils'
import { Input } from './Input'
import { Description } from './Description'
import { PasswordInput } from '../PasswordInput'
import DatePicker from './DatePicker'
import { GroupSelect } from './GroupSelect'

export interface TextFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  asChild?: boolean
  error?: string
  status?: 'success' | 'error'
  statusText?: string
  large?: boolean
}

// export interface LabelProps extends React.InputHTMLAttributes<HTMLSpanElement> {
//   asChild?: boolean
// }

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ status, statusText, disabled, children, large = false }) => {
    const childrenArray = getChildrenArray(children)

    const LabelComponent = childrenArray.find((child) => child?.type === Label)
    const InputComponent: typeof Input = childrenArray.find(
      (child) =>
        child?.type === Input ||
        child?.type === PasswordInput ||
        child?.type === DatePicker ||
        child?.type?.isFormControl,
    )
    const DescriptionComponent = childrenArray.find(
      (child) => child?.type === Description,
    )

    const SelectComponent = childrenArray.find(
      (child) => child?.type === GroupSelect,
    )

    const randomId = React.useId()
    const inputId = InputComponent?.props?.id ?? randomId
    return (
      <>
        {LabelComponent && (
          <Slot
            data-large={large ? true : undefined}
            htmlFor={inputId}
            className={cn(LabelComponent.className, 'mb-1')}
          >
            {LabelComponent}
          </Slot>
        )}
        {InputComponent && (
          <Slot
            id={inputId}
            data-status={status}
            status={status}
            disabled={disabled}
            large={large}
          >
            {InputComponent}
          </Slot>
        )}
        {SelectComponent && <Slot>{SelectComponent}</Slot>}
        {DescriptionComponent && <Slot>{DescriptionComponent}</Slot>}
        {status && statusText && (
          <Description data-status={status} className="flex items-center gap-1">
            {status === 'error' && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="size-4"
              >
                <path
                  fillRule="evenodd"
                  d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {status === 'success' && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="size-4"
              >
                <path
                  fillRule="evenodd"
                  d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span>{statusText}</span>
          </Description>
        )}
        {/* {restChildren} */}
      </>
    )
  },
)
TextField.displayName = 'TextField'

let TextFieldComponent = Object.assign(TextField, {
  Label,
  Input,
  Description,
  PasswordInput,
  DatePicker,
    GroupSelect,
})

// export { TextField, Label, Input }
export { TextFieldComponent as TextField }
